// ============================================================
// HOUSE OF CARDS — roomData v2 — THE FULL HOUSE
// All six rooms + the Mouth Door's three faces. Pure data.
//
// Hotspot fields:
//   id, label, x, y (percent), actions, desc, closeup
//   item        — item id granted by "take"
//   takeFlag    — flag set on take (default: took_<id>); App blocks re-takes
//   useTarget   — armed items can be used here
//   puzzle      — press-puzzle id handled by puzzleEngine ("drain","clock")
//   bookOrder   — study bookshelf sequence index
//   hiddenUntil — flag; invisible until true
//   removedBy   — flag; disappears once true
//   art         — future CDN image url (renders inside the card frame later)
// ============================================================

export const ITEMS = {
  rubber_ball:  { id: "rubber_ball",  name: "Rubber Ball",          desc: "Half-deflated. Still wants to bounce. Still wants to be thrown." },
  torn_page:    { id: "torn_page",    name: "Torn Page",            desc: "A child's handwriting: 'shelve the moons from dark to light.'" },
  crescent_key: { id: "crescent_key", name: "Crescent Winding Key", desc: "A winding key shaped like a crescent moon. Cold. Eager." },
  rust_key:     { id: "rust_key",     name: "Rust Key",             desc: "Fell out of the music box mid-lullaby. Smells like the kitchen." },

  tinder_box:   { id: "tinder_box",   name: "Tinder Box",           desc: "Flint, char-cloth, a candle stub. Fire, asking to be invited back in." },
  ice_block:    { id: "ice_block",    name: "Block of Ice",         desc: "Cold enough to ache. Something brass sleeps inside it." },
  brass_valve:  { id: "brass_valve",  name: "Brass Valve-Wheel",    desc: "Freed from the ice. Fits a bare spindle somewhere." },
  iron_crank:   { id: "iron_crank",   name: "Iron Crank",           desc: "Pulled from the drain. The house has been hiding its own machinery." },
  tarnished_key:{ id: "tarnished_key",name: "Tarnished Key",        desc: "Sent up on the dumbwaiter, on a doily, like room service." },
  tarnished_locket: { id: "tarnished_locket", name: "Tarnished Locket", desc: "Won't open. Engraved on the back: 'for my little brother.'" },
  black_key:    { id: "black_key",    name: "Black Iron Key",       desc: "From inside the clock. Cold enough to burn. It wants DOWN." },
  painted_rock: { id: "painted_rock", name: "The Painted Rock",     desc: "The gold bar you threw at her feet. It's here. It's heavier now." },
};

export const DOORS = [
  { id: "nursery", label: "Left Door I",    room: "nursery", lockedBy: null,            x: 16, y: 30 },
  { id: "study",   label: "Right Door I",   room: "study",   lockedBy: null,            x: 84, y: 30 },
  { id: "bath",    label: "Left Door II",   room: "bath",    lockedBy: "brass_valve",   x: 16, y: 55,
    lockHint: "No handle at all — just a bare spindle where a valve-wheel should sit. Steam hisses through the keyless gap.",
    unlockMsg: "The brass valve-wheel seats onto the spindle like it never left. You crank it — bolts withdraw like tendons relaxing. The bathroom exhales." },
  { id: "kitchen", label: "Right Door II",  room: "kitchen", lockedBy: "rust_key",      x: 84, y: 55 },
  { id: "master",  label: "Left Door III",  room: "master",  lockedBy: "tarnished_key", x: 16, y: 80,
    lockHint: "A fine door gone green with tarnish. The keyhole is shaped like an apology." },
  { id: "cellar",  label: "Right Door III", room: "cellar",  lockedBy: "black_key",     x: 84, y: 80,
    lockHint: "Black iron lock, frost on the plate. Cold air breathes OUT of this one. The house keeps something under itself.",
    unlockMsg: "The black key turns and the lock doesn't click — it sighs. Stairs, going down. Of course there are stairs going down." },
];

export const ROOMS = {
  // ── THE HALL ────────────────────────────────────────────────
  hall: {
    id: "hall",
    name: "The Hall",
    flavor: "Three doors a side. Veins under the wallpaper, keeping time with your pulse.",
    hotspots: [
      {
        id: "mouth_door",
        label: "The Mouth Door",
        x: 50, y: 22,
        actions: ["inspect", "use"],
        useTarget: true,
        special: "mouth",
        removedBy: "all_doors_open",
        desc: "A massive double door. The face is not carved. It is grown.",
        closeup:
          "Up close, the wood grain runs like skin. The mouth parts as you lean in.\n\n\"Six little teeth before you reach my tongue. Open them all, and I open too.\"\n\nIt smiles. You step back.",
      },
      {
        id: "mouth_door_open",
        label: "The Mouth Door",
        x: 50, y: 22,
        actions: ["inspect", "use"],
        useTarget: true,
        special: "mouth",
        hiddenUntil: "all_doors_open",
        removedBy: "chapter_one_done",
        roomChoices: [
          { id: "mouth_refuse_leave", label: "Refuse the toll. Leave through the builder's door.", once: "chapter_one_done", requiresFlag: "found_the_way_out", hint: "You found another way. Take it." },
          { id: "mouth_walk_away", label: "Tell it no. Just... no. And walk out the front.", once: "chapter_one_done", hint: "Deny it the ending it wants." },
        ],
        desc: "All six teeth are open. The face is drooling. It is VERY awake.",
        closeup:
          "The mouth hangs open now, breath like a furnace cooling.\n\n\"Six teeth. Well done, little landlord. Now — the toll. Every door pays a toll.\"\n\nIts eyes drop, deliberately, to your pockets.\n\n\"Feed me the thing you valued LEAST.\"",
      },
      {
        id: "mouth_door_eaten",
        label: "The Open Mouth",
        x: 50, y: 22,
        actions: ["inspect"],
        special: "mouth",
        hiddenUntil: "chapter_one_done",
        desc: "The double doors stand open. Beyond: stairs, going down. Further down than the cellar. Further down than anything.",
        closeup:
          "Warm air rises from below, carrying a smell like every dinner you ever missed — and very faintly, a voice you have not heard since the night of the fire.\n\nCHAPTER TWO descends from here. (Next build, partner.)",
      },
      {
        id: "hall_mushrooms",
        label: "Mushroom Cluster",
        x: 50, y: 86,
        actions: ["inspect"],
        roomChoices: [
          { id: "eat_mushrooms", label: "Eat from the ring", hint: "They lean toward the Mouth like an audience. (Star Power only.)" },
        ],
        desc: "Pale caps growing from the floorboards in a perfect ring.",
        closeup:
          "A fairy ring, indoors. The caps lean toward the Mouth Door like an audience. One of them appears to be... applauding? You decide not to look closer.",
      },
    ],
  },

  // ── THE NURSERY ─────────────────────────────────────────────
  nursery: {
    id: "nursery",
    name: "The Nursery",
    flavor: "A baby's room, preserved like an exhibit. Nothing in here has aged. That's the problem.",
    hotspots: [
      {
        id: "ball",
        label: "Rubber Ball",
        x: 24, y: 74,
        actions: ["inspect", "take"],
        item: "rubber_ball",
        takeFlag: "took_ball",
        removedBy: "took_ball",
        desc: "A red rubber ball, resting against the crib leg.",
        closeup: "It rolls an inch toward you when you look at it. Friendly? You pocket the thought, and maybe the ball.",
      },
      {
        id: "crib",
        label: "The Crib",
        x: 60, y: 40,
        actions: ["inspect", "use"],
        useTarget: true,
        roomChoices: [
          { id: "crib_rock", label: "Rock it gently", once: "chose_crib_rock", hint: "It might settle." },
          { id: "crib_tip", label: "Tip it over to be sure it's empty", once: "chose_crib_tip", hint: "Cruel. Final." },
        ],
        desc: "Iron crib, bars too tall. Built to keep something in.",
        closeup: "The blanket is tucked around a shape. You pull it back: nothing. The blanket settles back around the shape anyway.",
      },
      {
        id: "music_box",
        label: "Music Box",
        x: 80, y: 60,
        actions: ["inspect", "use"],
        useTarget: true,
        removedBy: "music_box_opened",
        desc: "A wound-down music box on the dresser.",
        closeup:
          "Brass and bone-inlay. The winding hole is shaped like a crescent moon. Something small rattles inside when you tilt it — it wants to give you what it's holding.",
      },
      {
        // ── FIRST SHADOW OBJECT ──────────────────────────────────
        // Exists ONLY in the unstable world (eyes: "unstable"). With
        // Tome's sight closed, the crib reads empty — the old mystery
        // holds. Once the lullaby opens his eyes, the thing that was
        // always rocking there becomes visible: a small shadow-shape,
        // rendered with the house's true biorganic/rotten treatment.
        // Pure lore/scare — never gates progression.
        id: "crib_shadow_child",
        label: "Something in the Crib",
        x: 60, y: 38,
        eyes: "unstable",
        actions: ["inspect"],
        scare: { chance: 0.5, once: true },
        desc: "A small shape, dark and breathing, where the blanket settled. It was always here. You just couldn't see it before.",
        closeup:
          "It is the size of a sleeping child and it is made of the same nothing the corners of this house are made of. It rocks, very slightly, though nothing rocks it.\n\nWhen your shadow falls across the bars, it turns its head toward you — a head with no face, only the suggestion of one, the way smoke suggests a shape before it disperses.\n\nIt has been waiting for someone who could finally see it. It seems, more than anything, relieved.",
      },
      {
        id: "dresser",
        label: "Dresser",
        x: 80, y: 82,
        actions: ["inspect", "take"],
        item: "torn_page",
        takeFlag: "took_page",
        desc: "Drawers swollen shut with damp. One is cracked open.",
        closeup:
          "Inside the cracked drawer: a page torn from a picture book. A child has written over the print:\n\n\"shelve the moons from dark to light.\"",
        takeLabel: "Take page",
      },
      {
        id: "suitcase",
        label: "Suitcase",
        x: 40, y: 84,
        actions: ["inspect"],
        desc: "A packed suitcase by the door. Someone meant to leave.",
        closeup: "Folded clothes for an adult and a child. A train ticket, unpunched, dated the night of the fire.",
      },
    ],
  },

  // ── THE STUDY ───────────────────────────────────────────────
  study: {
    id: "study",
    name: "The Study",
    flavor: "Books breathing on their shelves. A desk that remembers being a tree, and resents it.",
    hotspots: [
      { id: "book_new",      label: "Black-Spine Book",  x: 26, y: 34, actions: ["press"], bookOrder: 0, compact: true,
        desc: "Spine like a new moon — no light at all." },
      { id: "book_crescent", label: "Sliver-Spine Book", x: 42, y: 34, actions: ["press"], bookOrder: 1, compact: true,
        desc: "A thin crescent of silver leaf on black." },
      { id: "book_half",     label: "Half-Lit Book",     x: 58, y: 34, actions: ["press"], bookOrder: 2, compact: true,
        desc: "Spine half silver, half dark. Perfectly split." },
      { id: "book_full",     label: "Silver-Spine Book", x: 74, y: 34, actions: ["press"], bookOrder: 3, compact: true,
        desc: "Full silver. It glows faintly when the others are touched." },
      {
        id: "shelf_slot",
        label: "Hollow in the Shelf",
        x: 50, y: 54,
        actions: ["inspect"],
        hiddenUntil: "bookshelf_solved",
        desc: "A compartment has clicked open behind the books.",
        closeup: "Inside, on a bed of dried moss: a winding key shaped like a crescent moon. (It's in your inventory now.)",
      },
      {
        id: "tinder_box_spot",
        label: "Tinder Box",
        x: 88, y: 56,
        actions: ["inspect", "take"],
        item: "tinder_box",
        takeFlag: "took_tinder_box",
        removedBy: "took_tinder_box",
        desc: "A small tin on the mantel-shelf. Rattles dry.",
        closeup: "Flint, steel, char-cloth, a candle stub. The house burned once and KEPT the means to do it again. You take it before it gets ideas.",
      },
      {
        id: "desk",
        label: "The Desk",
        x: 30, y: 76,
        actions: ["inspect"],
        desc: "Ledgers, an inkwell grown over with moss, a land deed.",
        closeup: "The deed to this land. Sold and resold, the price dropping every time, the sellers' signatures getting shakier. The last line is your name. Your hand was steady. Fool.",
      },
      {
        id: "portrait",
        label: "Covered Portrait",
        x: 72, y: 76,
        actions: ["inspect"],
        scare: { chance: 0.7, once: true },
        roomChoices: [
          { id: "portrait_look_behind", label: "Look behind the painting", once: "found_landladys_office", hint: "It hangs a half-inch off the wall." },
        ],
        desc: "A painting under a sheet. The sheet moves wrong.",
        closeup: "You lift the corner. An old woman, shawls over shawls, eyes like wet stones. The plaque reads only: \"THE LANDLADY.\"\n\nThe frame hangs strangely proud of the wall, as if there's something behind it the wall would rather you didn't find.",
      },
      {
        id: "landlady_office",
        label: "The Landlady's Office",
        x: 50, y: 60,
        actions: ["inspect"],
        hiddenUntil: "found_landladys_office",
        scare: { chance: 0.4, once: true },
        roomChoices: [
          { id: "office_read_ledger", label: "Read the tenant ledger to the end", once: "read_the_ledger", hint: "Every name. All the way down." },
          { id: "office_burn_ledger", label: "Burn the ledger unread", once: "burned_the_ledger", hint: "Some things shouldn't be known." },
        ],
        desc: "A cramped office behind the portrait. A standing desk. A ledger. A hundred keys on a board, each labeled with a name.",
        closeup:
          "Behind the painting is a room the size of a confessional. A standing desk, a guttering candle that was not lit a moment ago, and on the wall a board of a hundred iron keys — each tagged with a name in the same careful handwriting that labeled the baby teeth.\n\nThe topmost key is tagged with the name of the family who built here first. The bottommost key, freshly cut, bright, untarnished, is tagged with YOURS.\n\nOn the standing desk: a ledger, open, a pen resting in the gutter as though someone stepped out mid-entry.",
      },
    ],
  },

  // ── THE KITCHEN ─────────────────────────────────────────────
  kitchen: {
    id: "kitchen",
    name: "The Kitchen",
    flavor: "Pots on the stove, still warm. Nobody has lived here for fifty years.",
    hotspots: [
      {
        id: "laundry_hoop",
        label: "Laundry Basket Hoop",
        x: 76, y: 28,
        actions: ["inspect", "use"],
        useTarget: true,
        desc: "A wicker laundry basket nailed above the pantry door, rim out. Somebody, sometime, played here.",
        closeup: "Regulation height, more or less. Chalk on the floor marks a free-throw line in a child's hand. The house kept this. Of everything, it kept THIS.",
      },
      {
        id: "stove",
        label: "The Stove",
        x: 30, y: 42,
        actions: ["inspect", "use"],
        useTarget: true,
        desc: "Six burners. Something simmering on the back one.",
        closeup: "You lift the lid. The pot is empty, and yet it simmers, and yet it smells like every dinner you ever missed. You put the lid back. Gently.\n\nThe burners themselves are dead cold. They'd take a light, if you had one.",
      },
      {
        id: "icebox",
        label: "The Icebox",
        x: 52, y: 60,
        actions: ["inspect", "take"],
        item: "ice_block",
        takeFlag: "took_ice_block",
        desc: "An antique icebox, frosted shut from the inside.",
        closeup: "You wrench it open. One shelf, one item: a block of ice the size of a loaf — and frozen in its heart, a brass valve-wheel.\n\nThe ice does not want to give it up. Maybe heat would change its mind.",
        takeLabel: "Take the ice",
      },
      {
        id: "dumbwaiter",
        label: "The Dumbwaiter",
        x: 88, y: 64,
        actions: ["inspect", "use"],
        useTarget: true,
        desc: "A small service hatch in the wall. Rope taut. Crank spindle EMPTY.",
        closeup: "Someone removed the crank — deliberately. The rope is taut, which means the car is loaded, which means something downstairs (upstairs?) wants to send you a delivery and CAN'T.\n\nYou hear, very faintly, a patient little bell.",
      },
      {
        id: "pantry_door",
        label: "Pantry Door",
        x: 76, y: 84,
        actions: ["inspect"],
        desc: "Chained shut. The chain is new. The lock is newer.",
        closeup: "Cold air leaks under it. Down past the shelves, stairs continue where no stairs should. The chain stays. For now. (Chapter Two business, partner.)",
      },
    ],
  },

  // ── THE BATHROOM ────────────────────────────────────────────
  bath: {
    id: "bath",
    name: "The Bathroom",
    flavor: "Tile gone green at the grout. The air is wet enough to drink and you absolutely should not.",
    hotspots: [
      {
        id: "mirror",
        label: "The Fogged Mirror",
        x: 30, y: 30,
        actions: ["inspect"],
        scare: { chance: 0.55, once: true },
        desc: "Fogged from a shower nobody took. Writing is appearing.",
        closeup:
          "You don't wipe it. You don't have to. Letters draw themselves through the fog, the way a finger would, if a finger were patient:\n\n\"IT WAS SEVEN O'CLOCK WHEN THE SMOKE CAME.\nTHE CLOCK UPSTAIRS STILL GRIEVES.\nLET IT CHIME ITS GRIEF.\"",
      },
      {
        id: "tub",
        label: "The Tub",
        x: 64, y: 44,
        actions: ["inspect", "use"],
        useTarget: true,
        removedBy: "tub_drained",
        desc: "A clawfoot tub, full to the lip with black water.",
        closeup: "The water isn't dirty — it's DARK, like it's full of night instead of dirt. Something glints at the bottom, much further down than the tub is deep. You are not putting your arm in there.\n\nThere's a drain chain bolted to the wall.",
      },
      {
        id: "tub_empty",
        label: "The Drained Tub",
        x: 64, y: 44,
        actions: ["inspect"],
        hiddenUntil: "tub_drained",
        desc: "Empty now. The black water went somewhere. Don't think about where.",
        closeup: "Clawfoot tub, bone dry in seconds, as if the house swallowed. Whatever was glinting down there is reachable now — check the drain.",
      },
      {
        id: "drain_chain",
        label: "Drain Chain",
        x: 86, y: 50,
        actions: ["press"],
        puzzle: "drain",
        removedBy: "tub_drained",
        desc: "A heavy pull-chain bolted to the tile. It leads INTO the tub water. Pull it.",
      },
      {
        id: "drain_crank",
        label: "Something in the Drain",
        x: 64, y: 68,
        actions: ["inspect", "take"],
        item: "iron_crank",
        takeFlag: "took_drain_crank",
        removedBy: "took_drain_crank",
        hiddenUntil: "tub_drained",
        desc: "An iron crank, wedged across the drain like it was HIDDEN there.",
        closeup: "An iron crank handle, exactly the kind a dumbwaiter spindle goes hungry for. Someone fed it to the tub to keep deliveries from arriving. The house has been arguing with itself.",
      },
      {
        id: "drain_locket",
        label: "Glint in the Drain",
        x: 80, y: 72,
        actions: ["inspect", "take"],
        item: "tarnished_locket",
        takeFlag: "took_drain_locket",
        removedBy: "took_drain_locket",
        hiddenUntil: "tub_drained",
        desc: "Something small and silver, caught on the drain cross.",
        closeup: "A tarnished locket on a snapped chain. It will not open — the seam is fused with age or grief, hard to say which. Engraved on the back, very small:\n\n\"for my little brother.\"",
      },
      {
        id: "medicine_cabinet",
        label: "Medicine Cabinet",
        x: 30, y: 64,
        actions: ["inspect"],
        desc: "Hanging open an inch. Glass jars inside.",
        closeup: "Cough syrup gone to amber. A rusted razor. And one clean jar, dusted daily by someone, labeled in careful handwriting:\n\n\"BABY TEETH — SIX.\"\n\nSix little teeth. You close the cabinet very, very gently.",
      },
    ],
  },

  // ── THE MASTER BEDROOM ──────────────────────────────────────
  master: {
    id: "master",
    name: "The Master Bedroom",
    flavor: "The biggest room and the coldest. The bed is made. The bed is always made.",
    hotspots: [
      {
        id: "clock",
        label: "Grandfather Clock",
        x: 26, y: 38,
        actions: ["press"],
        puzzle: "clock",
        removedBy: "clock_solved",
        desc: "Stopped. Pendulum still. Press the pendulum to make it chime.",
      },
      {
        id: "clock_open",
        label: "The Open Clock",
        x: 26, y: 38,
        actions: ["inspect"],
        hiddenUntil: "clock_solved",
        desc: "The case stands open. The grief is out.",
        closeup: "Behind the pendulum, a velvet pouch hung from a nail for fifty years. It held one thing: a black iron key, colder than the room. (It's in your pockets.)",
      },
      {
        id: "fireplace",
        label: "The Fireplace",
        x: 60, y: 50,
        actions: ["inspect", "use"],
        useTarget: true,
        desc: "Swept clean. Logs stacked. Laid for a fire nobody lit.",
        closeup: "Kindling arranged with care, fifty years patient. Above the mantle the wallpaper is discolored in long streaks, like something is written UNDER it — but you'd need firelight at just the right angle to read it.",
      },
      {
        id: "scorched_words",
        label: "Words Above the Mantle",
        x: 60, y: 26,
        actions: ["inspect"],
        hiddenUntil: "fireplace_lit",
        desc: "Firelight raking the wall reveals scorched handwriting under the paper.",
        closeup:
          "In the flicker, old scorch-marks bleed through the wallpaper — handwriting, burned into the plaster of the ORIGINAL house:\n\n\"SHE OFFERED US GOLD TOO.\nWE ALSO LAUGHED.\nDO NOT LAUGH.\"",
      },
      {
        id: "portrait_boys",
        label: "Portrait of Two Boys",
        x: 84, y: 40,
        actions: ["inspect", "use"],
        useTarget: true,
        roomChoices: [
          { id: "portrait_mourn", label: "Bow your head before it", once: "chose_portrait_mourn", hint: "Let yourself feel it." },
          { id: "portrait_tear", label: "Tear it down", once: "smashed_portrait", hint: "Refuse the sentiment." },
        ],
        desc: "Two boys, one tall, one small, painted mid-laugh.",
        closeup: "You know these faces. One of them is yours.\n\nThe painter caught the smaller boy mid-laugh, head thrown back — and around his neck, rendered in two careful brushstrokes, a little silver locket.",
      },
      {
        id: "wardrobe",
        label: "The Wardrobe",
        x: 84, y: 72,
        actions: ["inspect", "use"],
        useTarget: true,
        scare: { chance: 0.6, once: true },
        roomChoices: [
          { id: "builders_door_open", label: "Open the builder's door and leave", once: "chose_to_leave", requiresFlag: "found_the_way_out", hint: "Walk out. Now." },
          { id: "builders_door_seal", label: "Push the wardrobe back over it. Stay.", once: "chose_to_stay", requiresFlag: "found_the_way_out", hint: "See it through." },
        ],
        desc: "Double doors, slightly ajar. Coats inside. The coats are breathing.",
        closeup: "You open it fast, like ripping a bandage. Coats. Just coats. They sway from your draft, settle, and then — a beat too late — settle again.",
      },
      {
        id: "bed",
        label: "The Bed",
        x: 50, y: 78,
        actions: ["inspect", "use"],
        useTarget: true,
        scare: { chance: 0.5, once: true },
        desc: "Made with hospital corners. The pillows are dented. Both of them.",
        closeup: "You press a hand to the mattress. Warm. You take your hand back and decide, executively, permanently, never to think about this again.",
      },
    ],
  },

  // ── THE CELLAR ──────────────────────────────────────────────
  cellar: {
    id: "cellar",
    name: "The Cellar",
    flavor: "Below the new house: the bones of the old one. The ground breathes here. This is what she warned you about.",
    hotspots: [
      {
        id: "burned_beam",
        label: "The Burned Beam",
        x: 30, y: 36,
        actions: ["inspect"],
        desc: "A charred roof beam, kept like a relic, propped against the foundation.",
        closeup: "You know this beam. It tried to kill you when you were small.\n\nSomeone — something — dragged it down here and KEPT it. Carved into the char, in letters you feel more than read:\n\n\"IT MISSED. I DON'T.\"",
      },
      {
        id: "foundation",
        label: "The Old Foundation",
        x: 64, y: 32,
        actions: ["inspect"],
        roomChoices: [
          { id: "foundation_kneel", label: "Kneel on the warm stones and apologize", once: "chose_foundation_kneel", hint: "To the first family. To the ground." },
          { id: "foundation_spit", label: "Spit on it. It's just real estate.", once: "chose_foundation_spit", hint: "Defy the sob story." },
        ],
        desc: "The original house's footprint, black with the first fire. The new house grew on top of it like scar tissue.",
        closeup: "The stones are warm. Not fire-warm. BODY-warm. Between them, the veins from the walls upstairs all run down, and down, and gather, and descend into the dirt like roots.\n\nOr like they're holding something. Or holding it DOWN.",
      },
      {
        id: "painted_rock_spot",
        label: "Glint in the Dirt",
        x: 46, y: 66,
        actions: ["inspect", "take"],
        item: "painted_rock",
        takeFlag: "took_painted_rock",
        removedBy: "took_painted_rock",
        desc: "Half-buried in the foundation dirt: a bar of dull gold.",
        closeup: "You know this bar. You THREW this bar. At her feet, in the sunlight, on your porch — and here it is, under the house, buried like a seed.\n\nYou dig it out. It is much, much heavier than it was.\n\nYou do not laugh this time.",
        takeLabel: "Take the bar",
      },
      {
        id: "root_veins",
        label: "The Root of the Veins",
        x: 80, y: 64,
        actions: ["inspect", "use"],
        useTarget: true,
        roomChoices: [
          { id: "veins_listen", label: "Press your ear to the roots and listen", once: "chose_veins_listen", hint: "Hear what it's asking." },
        ],
        desc: "Every vein in every wall, gathered here, plunging into the earth.",
        closeup: "You put your ear near them, against every instinct you own.\n\nA heartbeat. Slow. Patient. Not the house's.\n\nBeneath it, fainter — somebody humming the music box lullaby, the way you'd hum to keep a child calm.\n\nOr to keep one asleep.",
      },
    ],
  },
};
